// src/components/StartHereCard.jsx
//
// Per-pack "Start Here" guide. Same DATA and same rules as before -- it reads the
// pack's admin-authored `startHere` ({ title?, subtitle?, steps?[] }), renders ONLY
// when non-empty (D2-a) so nothing demo-looking ever shows unless Danielle filled and
// saved it, and keeps the permanent per-pack "Don't show this again" dismiss.
//
// lesson-3a-layout changed only WHERE it lives: it was a full-width band above the
// lesson, pushing the content down on every visit. It is now a chip in the sections
// list that opens the same steps in a dialog. The steps are worth reading once; they
// were not worth the first screen of every lesson, every day.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReadingPrefsContext } from '../pages/useReadingPrefs.js';
import { portalSkinClass } from '../pages/readingPrefs.js';

export default function StartHereCard({ packId, startHere, routeAction }) {
    const steps = Array.isArray(startHere && startHere.steps)
        ? startHere.steps.filter((s) => typeof s === 'string' && s.trim())
        : [];
    const title = (startHere && typeof startHere.title === 'string' && startHere.title.trim()) || '';
    const subtitle = (startHere && typeof startHere.subtitle === 'string' && startHere.subtitle.trim()) || '';

    const dismissKey = `btl_v2_startHereDismissed_${packId}`;
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(dismissKey) === 'true'; } catch { return false; }
    });
    const [open, setOpen] = useState(false);

    // D2-a: hidden unless there is real authored content.
    if (dismissed || (!title && steps.length === 0)) return null;

    const handleDismiss = () => {
        if (!confirm("Hide 'Start Here' permanently for this pack? You won't see this guide again.")) return;
        try { localStorage.setItem(dismissKey, 'true'); } catch { /* ignore */ }
        setDismissed(true);
        setOpen(false);
    };

    return (
        <>
            <button type="button" onClick={() => setOpen(true)}
                className="w-full mb-1 flex items-center gap-2 px-3 py-2 rounded-full border border-book-accent/40 text-left hover:bg-book-accent/10 transition-colors">
                <span className="font-display text-book-accent text-xs font-semibold truncate">
                    {title || 'Start here'}
                </span>
            </button>
            {open && (
                <StartHereDialog title={title || 'Start Here'} subtitle={subtitle} steps={steps}
                    onClose={() => setOpen(false)} onDismiss={handleDismiss}
                    routeAction={routeAction ? { ...routeAction, onOpen: () => { setOpen(false); routeAction.onOpen(); } } : null} />
            )}
        </>
    );
}

// The same steps, in a centred dialog. Portalled to document.body so the fixed overlay
// is positioned against the VIEWPORT, not the reader shell's transformed
// .page-fade-in ancestor -- and the reader's theme skin is re-applied, since a portal
// preserves React context but not CSS ancestry (mirrors EssayModal / ReaderAiDrawer).
function StartHereDialog({ title, subtitle, steps, onClose, onDismiss, routeAction }) {
    const { colorMode } = React.useContext(ReadingPrefsContext);
    const skin = portalSkinClass(colorMode);
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return createPortal(
        <div className={skin}>
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
                onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
                <div onClick={(e) => e.stopPropagation()}
                    className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)]">
                    <div className="flex items-start justify-between gap-3 px-5 pt-5">
                        <div className="min-w-0">
                            <h3 className="font-display text-book-text text-lg font-bold leading-tight">{title}</h3>
                            {subtitle && <p className="font-serif italic text-book-muted text-sm mt-1">{subtitle}</p>}
                        </div>
                        <button onClick={onClose} aria-label="Close"
                            className="flex-shrink-0 font-display text-book-muted text-xs font-semibold hover:text-book-accent">
                            Close
                        </button>
                    </div>
                    {steps.length > 0 && (
                        <ol className="px-5 pt-4 space-y-2 font-serif text-book-text text-sm leading-relaxed">
                            {steps.map((step, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="font-display text-book-accent text-xs flex-shrink-0 mt-0.5 font-semibold">{String(i + 1).padStart(2, '0')}</span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                    <div className="mt-5 px-5 py-4 border-t border-book-line flex flex-wrap items-center justify-between gap-4">
                        {routeAction && <button type="button" onClick={routeAction.onOpen} className="px-4 py-2 border border-book-accent rounded-full font-display text-sm text-book-accent">{routeAction.label}</button>}
                        <button onClick={onDismiss} className="font-serif italic text-book-muted text-xs hover:text-book-accent transition-colors">
                            Don't show this again
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
